import { Typography } from "@shared/ui.primitives";
import React from "react";

import * as styles from "./StrikethroughPrice.css";
import {
  getDiscountedPriceVariant,
  originalPriceVariant
} from "./StrikethroughPrice.utils";

type Props = {
  variant?:
    | "verticalProductCard"
    | "topBlock"
    | "horizontalProductCard"
    | "basketCard"
    | "pricingOptionCard";
  price: string;
  discountedPrice?: string;
  dataTestId?: string;
};

/**
 * Displays a price with strikethrough styling alongside a discounted price.
 * Supports multiple display variants for different UI contexts:
 * - verticalProductCard: Default variant for vertical product cards
 * - topBlock: Variant for top block sections
 * - horizontalProductCard: Variant for horizontal product cards
 * - basketCard: Variant for basket/cart items
 * - pricingOptionCard: Variant for pricing option cards
 *
 * @param {Props} props - Props for the StrikethroughPrice component.
 * @param {string} [props.variant="verticalProductCard"] - Display variant that determines typography styling.
 * @param {string} props.price - Original price to display with strikethrough.
 * @param {string} props.discountedPrice - Discounted price to display prominently.
 * @param {string} [props.dataTestId] - Optional test ID for testing purposes.
 * @returns {JSX.Element} The rendered strikethrough price component.
 */
export const StrikethroughPrice: React.FC<Props> = ({
  variant = "verticalProductCard",
  price,
  discountedPrice,
  dataTestId
}) => {
  return (
    <div className={styles.container({ variant })} data-testid={dataTestId}>
      <Typography variant={originalPriceVariant(variant)}>{price}</Typography>
      {discountedPrice && (
        <Typography variant={getDiscountedPriceVariant(variant)}>
          {discountedPrice}
        </Typography>
      )}
    </div>
  );
};
